import React, { FC, useMemo } from 'react'
import {createUseStyles} from 'react-jss'
import classnames from 'classnames'
import RenderContext from './RenderContext'

import {
  ILayout,
  ILayout_Render_Context,
  ILayout_Render_Context_With_Options,
  ILayout_Row,
  ILayout_Cell,
  ILayout_RenderMap,
  ILayout_RenderMode,
  ILayout_Options,
} from '../types/layout'

import {
  useRowTableStyles,
  useRowStyles,
  useCellTableStyles,
  useCellStyles,
  isNodeRow,
} from './utils'

import RenderCell from './RenderCell'

import {
  LAYOUT_OPTIONS_MAP,
  LAYOUT_OPTIONS_DEFAULT,
  LAYOUT_OPTIONS_WIDTH_DEFAULT,
  LAYOUT_RENDER_MODE_TABLE,
} from '../constants'

interface LayoutRenderCellProps {
  cell: ILayout_Cell,
  mode?: ILayout_RenderMode,
  percentWidth: number,
  renderers?: Partial<ILayout_RenderMap>,
}

const LayoutRenderCellTable: FC<React.PropsWithChildren<LayoutRenderCellProps>> = ({
  cell,
  percentWidth,
  children,
}) => {
  const classes = useCellTableStyles({
    config: cell.config || {},
    percentWidth,
  })
  return (
    <td className={ classes.td }>
      { children }
    </td>
  )
}

const LayoutRenderCellFlex: FC<React.PropsWithChildren<LayoutRenderCellProps>> = ({
  cell,
  percentWidth,
  children,
}) => {
  const classes = useCellStyles({
    config: cell.config || {},
    percentWidth,
  })
  return (
    <div className={ classnames('Layout-Editor-Node', 'Layout-Editor-Cell', classes.cell) }>
      <div className={ classnames('Cell-Inner', classes.content) }>
        { children }
      </div>
    </div>
  )
}

const LayoutRenderCell: FC<React.PropsWithChildren<LayoutRenderCellProps>> = ({
  cell,
  mode = LAYOUT_RENDER_MODE_TABLE,
  percentWidth,
  renderers,
  children,
}) => {
  const UseComponent = mode == LAYOUT_RENDER_MODE_TABLE ?
    LayoutRenderCellTable :
    LayoutRenderCellFlex
  return (
    <UseComponent
      cell={ cell }
      percentWidth={ percentWidth }
      renderers={ renderers }
    >
      { children }
    </UseComponent>
  )
}

interface LayoutRenderRowProps {
  row: ILayout_Row,
  mode?: ILayout_RenderMode,
  renderers?: Partial<ILayout_RenderMap>,
}

const LayoutRenderRowTable: FC<React.PropsWithChildren<LayoutRenderRowProps>> = ({
  row,
  children,
}) => {
  const classes = useRowTableStyles({
    config: row.config,
  })
  return (
    <div className={ classes.row }>
      <table width="100%" cellPadding="0" cellSpacing="0" className={ classes.table }>
        <tbody>
          <tr>
            {
              children
            }
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const LayoutRenderRowFlex: FC<React.PropsWithChildren<LayoutRenderRowProps>> = ({
  row,
  children, 
}) => {
  const classes = useRowStyles({
    config: row.config,
  })
  return (
    <div className={ classnames('Layout-Editor-Node', 'Layout-Editor-Row', classes.row) }>
      <div className={ classes.cellContainer }>
        <div className={ classes.margin }></div>
        <div className={ classnames('Row-Inner', classes.cells) }>
          { children }
        </div>
        <div className={ classes.margin }></div>
      </div>
    </div>
  )
}

const LayoutRenderRow: FC<React.PropsWithChildren<LayoutRenderRowProps>> = ({
  row,
  mode = LAYOUT_RENDER_MODE_TABLE,
  renderers,
}) => {
  
  const totalWidth = row.cells.reduce<number>((all, node) => {
    return all + (node.config?.width || 1)
  }, 0)
  const nodeWidths = row.cells.map((node) => {
    const nodeWidth = node.config?.width || 1
    return Math.floor((nodeWidth / totalWidth) * 100)
  })

  const cells = row.cells.map((node, i) => {
    const nodeWidth = nodeWidths[i]

    return (
      <LayoutRenderCell
        key={ i }
        cell={ isNodeRow(node) ? {type: 'text', data: {}} : node }
        mode={ mode }
        percentWidth={ nodeWidth }
        renderers={ renderers }
      >
        {
          isNodeRow(node) ? (
            <LayoutRenderRow
              row={ node }
              mode={ mode }
              renderers={ renderers }
            />
          ) : (
            <RenderCell
              editMode={ false }
              cell={ node }
              renderers={ renderers }
            />
          )
        }
      </LayoutRenderCell>
    )
  })

  return mode == LAYOUT_RENDER_MODE_TABLE ? (
    <LayoutRenderRowTable
      row={ row }
    >
      { cells }
    </LayoutRenderRowTable>
  ) : (
    <LayoutRenderRowFlex
      row={ row }
    >
      { cells }
    </LayoutRenderRowFlex>
  )
}

export interface ICSSProps_Main {
  width: string | number,
}

export const useMainStyles = createUseStyles({
  root: {
    width: '100%',
  },
  layout: ({width}:ICSSProps_Main) => ({
    width,
    marginLeft: 'auto',
    marginRight: 'auto',
  }),
})

interface LayoutRenderProps {
  data: ILayout,
  renderers?: Partial<ILayout_RenderMap>,
  context: ILayout_Render_Context,
}

const LayoutRender: FC<React.PropsWithChildren<LayoutRenderProps>> = ({
  data,
  renderers,
  context,
}) => {

  let useOptions: ILayout_Options = LAYOUT_OPTIONS_DEFAULT

  if(data.optionsName && LAYOUT_OPTIONS_MAP[data.optionsName]) {
    useOptions = LAYOUT_OPTIONS_MAP[data.optionsName]
  }
  else if(data.options) {
    useOptions = data.options
  }

  const width = useOptions?.fullWidth ?
    '100%' :
    useOptions?.width || LAYOUT_OPTIONS_WIDTH_DEFAULT

  const mode = useOptions?.renderMode || LAYOUT_RENDER_MODE_TABLE

  const classes = useMainStyles({
    width,
  })

  const useContextData = useMemo<ILayout_Render_Context_With_Options>(() => {
    return Object.assign({}, context, {
      options: useOptions,
    })
  }, [
    context,
    useOptions,
  ])

  return (
    <RenderContext.Provider value={ useContextData }>
      <div className={ classnames('Layout-Container', classes.root) }>
        <div className={ classes.layout }>
          {
            data.rows.map((row, i) => {
              return (
                <LayoutRenderRow
                  key={ i }
                  mode={ mode }
                  renderers={ renderers }
                  row={ row }
                />
              )
            })
          }
        </div>
      </div>
    </RenderContext.Provider>
  )

}

export default LayoutRender